import { CartItem, ICartItem, IMenuItemOptionGroup, IOnlineMenuItem } from "@dining/api-client";
import * as yup from "yup";

export interface ICartItemOption {
  id: string;
  name: string;
  amount: number;
  isSelected: boolean;
  price: {
    amount: number;
    currencyUnit: string;
  };
}

export interface ICartItemOptionGroup {
  id: string;
  name: string;
  isSingleSelect: boolean;
  minimum: number;
  maximum: number;
  options: ICartItemOption[];
}

export interface ICartItemFormValues {
  optionGroups: ICartItemOptionGroup[];
  customInstructions?: string;
}

const cartItemOptionSchema: yup.ObjectSchema<ICartItemOption> = yup.object().shape({
  id: yup.string().defined(),
  name: yup.string().defined(),
  amount: yup.number().defined(),
  isSelected: yup.boolean().defined(),
  price: yup
    .object()
    .shape({
      amount: yup.number().defined(),
      currencyUnit: yup.string().defined(),
    })
    .defined()
    .default({ amount: 0, currency: "$" }),
});

const cartItemOptionGroupSchema: yup.Lazy<ICartItemOptionGroup> = yup.lazy(
  (value: IMenuItemOptionGroup) =>
    yup
      .object()
      .shape({
        id: yup.string().defined(),
        isSingleSelect: yup.boolean().defined(),
        minimum: yup.number().defined(),
        maximum: yup.number().defined(),
        name: yup.string().defined(),
        options: yup.array().of(cartItemOptionSchema).defined(),
      })
      .test({
        name: "minimum",
        params: { minimum: value.minimum },
        message: "Please select at least ${minimum} options",
        test: (o) => {
          if (o.minimum > 0) {
            const selectedCount = o.options.filter((o) => o.isSelected).length;
            return selectedCount < o.minimum ? false : true;
          }
          return true;
        },
      })
      .test({
        name: "maximum",
        params: { maximum: value.maximum },
        message: "Please select at most ${maximum}",
        test: (o) => {
          if (o.maximum > 0) {
            const selectedCount = o.options?.filter((o) => o.isSelected).length;
            return selectedCount > o.maximum ? false : true;
          }
          return true;
        },
      })
      .required(),
);

export const cartItemFormSchema: yup.ObjectSchema<ICartItemFormValues> = yup.object({
  optionGroups: yup.array().of(cartItemOptionGroupSchema).defined(),
  customInstructions: yup.string().max(250),
});

export const createInitialValues: (item: IOnlineMenuItem) => ICartItemFormValues = (
  item: IOnlineMenuItem,
) => {
  return {
    optionGroups:
      item.options?.map((o) =>
        cartItemOptionGroupSchema.cast(o, {
          stripUnknown: true,
        }),
      ) || [],
  };
};

export const createCartItem: (
  values: ICartItemFormValues,
  item: IOnlineMenuItem,
  amountWithOptions: number,
) => ICartItem = (values, item, amountWithOptions) => {
  return CartItem.fromJS({
    ...item,
    amountWithOptions,
    customInstruction: values.customInstructions,
    options: values.optionGroups.map((optionGroup) => {
      const matchingItemOptionGroup = item.options?.find((option) => optionGroup.id === option.id);
      return {
        ...matchingItemOptionGroup,
        ...optionGroup,
        options: optionGroup.options.map((option) => {
          const matchingItemOption = matchingItemOptionGroup?.options?.find(
            (o) => option.id === o.id,
          );
          return {
            ...matchingItemOption,
            ...option,
          };
        }),
      };
    }),
  });
};
